import React, { useEffect, useMemo, useState } from "react";

import { Button, Modal } from "@repo/ui";
import {
  Stack,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from "@mui/material";

const CANCELLATION_REASONS: Array<{ label: string; value: string }> = [
  { label: "Doesn't fit my budget", value: "budget" },
  { label: "Missing features I need", value: "missing_features" },
  { label: "Technical issues or bugs", value: "technical_issues" },
  { label: "No longer using the platform", value: "no_longer_using" },
  { label: "Other (please explain)", value: "other" },
];

type CancelSubscriptionModalProps = {
  open: boolean;
  onClose: () => void;
  onConfirmCancel: (payload: { reason: string; feedback?: string }) => void;
  onAcceptOffer: () => void;
  isCancelling?: boolean;
  isAcceptingOffer?: boolean;
  activeUntil?: string | null;
};

export const CancelSubscriptionModal = ({
  open,
  onClose,
  onConfirmCancel,
  onAcceptOffer,
  isCancelling = false,
  isAcceptingOffer = false,
  activeUntil = null,
}: CancelSubscriptionModalProps) => {
  const [step, setStep] = useState<"offer" | "feedback">("offer");
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [feedback, setFeedback] = useState<string>("");

  useEffect(() => {
    if (!open) {
      setStep("offer");
      setSelectedReason("");
      setFeedback("");
    }
  }, [open]);

  const isOtherReason = selectedReason === "other";

  const canConfirmCancellation = useMemo(() => {
    if (!selectedReason) return false;
    if (isOtherReason) {
      return feedback.trim().length > 0;
    }
    return true;
  }, [selectedReason, feedback, isOtherReason]);

  const handleCancelClick = () => {
    if (step === "offer") {
      setStep("feedback");
      return;
    }

    if (!canConfirmCancellation || isCancelling) return;

    onConfirmCancel({
      reason: selectedReason,
      feedback: feedback.trim() || undefined,
    });
  };

  const handleClose = () => {
    if (isCancelling || isAcceptingOffer) return;
    onClose();
  };

  return (
    <Modal
      title={
        step === "offer" ? "Enjoy a free month on us" : "Cancel subscription"
      }
      open={open}
      onClose={handleClose}
      sx={{ width: 520 }}
      actions={
        step === "offer" ? (
          <>
            <Button
              variant="secondary"
              onClick={handleCancelClick}
              disabled={isCancelling || isAcceptingOffer}
            >
              Cancel subscription
            </Button>
            <Button
              variant="primary"
              onClick={onAcceptOffer}
              disabled={isCancelling || isAcceptingOffer}
            >
              {isAcceptingOffer ? "Applying..." : "Get a free month"}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleCancelClick}
              disabled={!canConfirmCancellation || isCancelling}
            >
              {isCancelling ? "Canceling..." : "Confirm cancelation"}
            </Button>
          </>
        )
      }
    >
      {step === "offer" ? (
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Thinking about canceling? Stay subscribed and enjoy one month free
            to continue experiencing all the benefits Vera has to offer.
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Typography variant="body3" color="text.secondary">
            We're sorry to see you go. Your subscription will remain active
            {activeUntil ? ` until ${activeUntil}.` : "."} You can resubscribe
            anytime to continue enjoying the benefits of Vera.
          </Typography>
          <Typography variant="body3" fontWeight={500}>
            Tell us why you're canceling
          </Typography>
          <RadioGroup
            value={selectedReason}
            onChange={(event) => setSelectedReason(event.target.value)}
          >
            <Stack spacing={1.5}>
              {CANCELLATION_REASONS.map((reason) => (
                <FormControlLabel
                  key={reason.value}
                  value={reason.value}
                  control={<Radio />}
                  label={
                    <Typography variant="body3">{reason.label}</Typography>
                  }
                />
              ))}
            </Stack>
          </RadioGroup>
          {isOtherReason && (
            <TextField
              multiline
              minRows={3}
              value={feedback}
              onChange={(event) => setFeedback(event.target.value)}
              placeholder="Share feedback"
            />
          )}
        </Stack>
      )}
    </Modal>
  );
};
